import cx from 'classnames'

import { workspacesSet } from 'redux/actions'
import { collectResults } from 'utils/query'

import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { useDispatch } from 'react-redux'
import { useTransformedBrandPlanSchedules } from 'screens/BrandKits/components/BrandPlanTimeline/useTransformedBrandPlanSchedules'

import { ConfirmPauseModal } from './ConfirmPauseModal/ConfirmPauseModal'
import { ConfirmResumeModal } from './ConfirmResumeModal/ConfirmResumeModal'
import SectionStyles from 'components/Settings/Section.module.scss'
import Styles from './AutopilotSettings.module.scss'
import DangerZoneStyles from 'components/Settings/DangerZone/DangerZone.module.scss'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import dayjs from 'dayjs'
import { useModals } from 'almanac-ui/Modal'
import { TransformedBrandPlanScheduleListItem } from 'screens/BrandKits/components/BrandPlanTimeline/Types'
import { useQueryClient } from 'react-query'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { observer } from 'mobx-react-lite'
import { useTogglePostApproval } from 'hooks/useTogglePostApproval'
import { ArrowRefresh } from 'blaze-ui/icons'
import CalendarPost from 'blaze-ui/icons/20/CalendarPost'

export const AutopilotSettings = observer(({ brandPlanId }: { brandPlanId: number }) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const workspace = useCurrentWorkspace()
  const workspaces = useWorkspaces()
  const { openModal, closeModal } = useModals()
  const schedulesQuery = useTransformedBrandPlanSchedules({ brandPlanId })
  const schedules = collectResults(schedulesQuery, (data) => data.schedules)
  const currentSchedule = schedules.find((s: TransformedBrandPlanScheduleListItem) => s.is_current)
  const currentScheduleIndex = currentSchedule ? schedules.indexOf(currentSchedule) : null
  const nextSchedule =
    currentScheduleIndex !== null && currentScheduleIndex !== -1 ? schedules[currentScheduleIndex - 1] : null
  const togglePostApproval = useTogglePostApproval()

  if (!workspace?.is_virtual_marketer) return null

  const toggleAutopilot = async (
    newPauseAutopilotGenerateAt: string | null | undefined,
    newPauseAutopilotPublishAt: string | null | undefined,
    newResumeAutopilotGenerateAt: string | null | undefined,
    newResumeAutopilotPublishAt: string | null | undefined,
  ) => {
    if (!workspace) return

    const prevPauseAutopilotGenerateAt = workspace.pause_autopilot_generate_at
    const prevPauseAutopilotPublishAt = workspace.pause_autopilot_publish_at
    const prevResumeAutopilotGenerateAt = workspace.resume_autopilot_generate_at
    const prevResumeAutopilotPublishAt = workspace.resume_autopilot_publish_at

    dispatch(
      workspacesSet(
        workspaces.map((ws) =>
          ws.id === workspace.id
            ? {
                ...ws,
                pause_autopilot_generate_at: newPauseAutopilotGenerateAt,
                pause_autopilot_publish_at: newPauseAutopilotPublishAt,
                resume_autopilot_generate_at: newResumeAutopilotGenerateAt,
                resume_autopilot_publish_at: newResumeAutopilotPublishAt,
              }
            : ws,
        ),
      ),
    )

    const res = await sendToApi<void>(`/w/${workspace.id}/update`, {
      method: 'PUT',
      body: {
        pause_autopilot_generate_at: newPauseAutopilotGenerateAt,
        pause_autopilot_publish_at: newPauseAutopilotPublishAt,
        resume_autopilot_generate_at: newResumeAutopilotGenerateAt,
        resume_autopilot_publish_at: newResumeAutopilotPublishAt,
      },
    })

    if (res.type !== SUCCESS_RESPONSE_TYPE) {
      showToastMessage("We couldn't save your settings.", 'error')
      dispatch(
        workspacesSet(
          workspaces.map((ws) =>
            ws.id === workspace.id
              ? {
                  ...ws,
                  pause_autopilot_generate_at: prevPauseAutopilotGenerateAt,
                  pause_autopilot_publish_at: prevPauseAutopilotPublishAt,
                  resume_autopilot_generate_at: prevResumeAutopilotGenerateAt,
                  resume_autopilot_publish_at: prevResumeAutopilotPublishAt,
                }
              : ws,
          ),
        ),
      )
    }
  }

  const toggleAllAutopilot = () => {
    const current = !!workspace.pause_autopilot_generate_at && !!workspace.pause_autopilot_publish_at
    const value = current ? null : dayjs().toISOString()

    if (value) {
      openModal(
        ConfirmPauseModal,
        {
          variant: 'all',
          onConfirm: () => {
            toggleAutopilot(value, value, null, null)
            closeModal()
          },
        },
        {
          newStack: true,
        },
      )
      return
    }

    if (nextSchedule?.startDate) {
      openModal(
        ConfirmResumeModal,
        {
          variant: 'all',
          onConfirm: (resumeAt) => {
            const resumeAtValue = resumeAt === 'next-week' ? nextSchedule?.startDate.toISOString() : null
            toggleAutopilot(value, value, resumeAtValue, resumeAtValue)
            closeModal()
          },
          startDate: nextSchedule.startDate,
        },
        {
          newStack: true,
        },
      )
    } else {
      // No next schedule available, resume immediately
      toggleAutopilot(value, value, null, null)
    }
  }

  const togglePauseAutopilotGenerate = () => {
    const pauseAt = workspace.pause_autopilot_generate_at ? null : dayjs().toISOString()

    if (pauseAt) {
      openModal(
        ConfirmPauseModal,
        {
          startDate: nextSchedule?.startDate,
          variant: 'generate',
          onConfirm: () => {
            toggleAutopilot(pauseAt, workspace.pause_autopilot_publish_at, null, workspace.resume_autopilot_publish_at)
            closeModal()
          },
        },
        {
          newStack: true,
        },
      )
      return
    }

    if (nextSchedule?.startDate) {
      openModal(
        ConfirmResumeModal,
        {
          variant: 'generate',
          onConfirm: (resumeAt) => {
            const resumeGenerateAtValue = resumeAt === 'next-week' ? nextSchedule?.startDate.toISOString() : null
            toggleAutopilot(
              pauseAt,
              workspace.pause_autopilot_publish_at,
              resumeGenerateAtValue,
              workspace.resume_autopilot_publish_at,
            )
            closeModal()
          },
          startDate: nextSchedule.startDate,
        },
        {
          newStack: true,
        },
      )
    } else {
      // No next schedule available, resume immediately
      toggleAutopilot(pauseAt, workspace.pause_autopilot_publish_at, null, workspace.resume_autopilot_publish_at)
    }
  }

  const togglePauseAutopilotPublish = () => {
    const pauseAt = workspace.pause_autopilot_publish_at ? null : dayjs().toISOString()

    if (pauseAt) {
      openModal(
        ConfirmPauseModal,
        {
          variant: 'publish',
          onConfirm: () => {
            toggleAutopilot(
              workspace.pause_autopilot_generate_at,
              pauseAt,
              workspace.resume_autopilot_generate_at,
              null,
            )
            queryClient.invalidateQueries(filesKeys.all(workspace.id))
            closeModal()
          },
        },
        {
          newStack: true,
        },
      )
      return
    }

    if (nextSchedule?.startDate) {
      openModal(
        ConfirmResumeModal,
        {
          variant: 'publish',
          onConfirm: (resumeAt) => {
            const resumePublishAtValue = resumeAt === 'next-week' ? nextSchedule.startDate.toISOString() : null
            toggleAutopilot(
              workspace.pause_autopilot_generate_at,
              pauseAt,
              workspace.resume_autopilot_generate_at,
              resumePublishAtValue,
            )
            queryClient.invalidateQueries(filesKeys.all(workspace.id))
            closeModal()
          },
          startDate: nextSchedule.startDate,
        },
        {
          newStack: true,
        },
      )
    } else {
      // No next schedule available, resume immediately
      toggleAutopilot(workspace.pause_autopilot_generate_at, pauseAt, workspace.resume_autopilot_generate_at, null)
      queryClient.invalidateQueries(filesKeys.all(workspace.id))
    }
  }

  const autopilotChecked = !workspace.pause_autopilot_generate_at || !workspace.pause_autopilot_publish_at

  return (
    <div className={SectionStyles.sectionRow}>
      <div className={cx(SectionStyles.alignTop, SectionStyles.noPadding)}>
        <div className={Styles.enableToggleContainer}>
          <Heading level={3}>Autopilot</Heading>
          <div className={Styles.enableToggleContent}>
            <Text variant="label" color="var(--dark-40)">
              Autopilot {autopilotChecked ? 'enabled' : 'disabled'}
            </Text>
            <ToggleButton checked={autopilotChecked} name="enable-autopilot" handleChange={toggleAllAutopilot} />
          </div>
        </div>
        <div className={DangerZoneStyles.sectionContent}>
          <p className={cx(DangerZoneStyles.warningText, DangerZoneStyles.muted)}>
            Blaze automatically generates and publish your content. Turn off to pause both automated actions.
          </p>
        </div>
        <div className={Styles.togglesContainer}>
          <div className={Styles.toggleContainer}>
            <div className={Styles.toggleIconContainer}>
              <ArrowRefresh size={20} />
              <Text variant="secondary">Auto Content Generation</Text>
            </div>
            <ToggleButton
              checked={!workspace.pause_autopilot_generate_at}
              name="auto-content-generation"
              handleChange={togglePauseAutopilotGenerate}
              size="sm-md"
            />
          </div>
          <div className={Styles.toggleContainer}>
            <div className={Styles.toggleIconContainer}>
              <CalendarPost size={20} />
              <Text variant="secondary">Auto Publishing</Text>
            </div>
            <ToggleButton
              checked={!workspace.pause_autopilot_publish_at}
              name="auto-publishing"
              handleChange={togglePauseAutopilotPublish}
              size="sm-md"
            />
          </div>
          <div className={Styles.toggleContainer}>
            <Text variant="secondary">Approve content before auto-posting</Text>
            <ToggleButton
              checked={!!workspace.post_approval_enabled}
              name="post-approval"
              handleChange={togglePostApproval}
              size="sm-md"
            />
          </div>
        </div>
      </div>
    </div>
  )
})
